/**
 * Helper functions for user-related operations.
 * @module userHelper
 */

import groq from "groq"

import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { Roles } from "../../shared/permissions/permissionsConfig"
import { getAdmAssignedCustomers } from "../../shared/utils/getAdmAssignedCustomers"
import { getCustomer } from "../../shared/utils/getCustomer"

const commerceApi = CommerceService.api()

/**
 * Get the role of a user based on their customer group.
 * @param {Object} options - The options for getting the role.
 * @param {Object} options.customerGroup - The customer group object.
 * @returns {string} The role of the user.
 */
export const getRole = ({ customerGroup }) => {
  switch (customerGroup?.key) {
    case "adm_user":
      return Roles.ADM
    case "adm_regionallead":
      return Roles.REGIONAL_LEAD
    case "adm_admin":
      return Roles.ADMIN
    case "idm_user":
      return Roles.IDM_USER
    case "idm_disponent":
      return Roles.IDM_DISPONENT
    case "idm_lead":
      return Roles.IDM_LEAD
    case "idm_theke":
      return Roles.IDM_THEKE
    case "adm_gvl":
      return Roles.ADM_GVL
    case "idm_springer":
      return Roles.IDM_SPRINGER
    case "idm_springer_lead":
      return Roles.IDM_SPRINGER_LEAD
    case "idm_procurement":
      return Roles.IDM_PROCUREMENT
    default:
      return "CUSTOMER"
  }
}

/**
 * Get a user by their ID.
 * @param {Object} options - The options for getting the user.
 * @param {string} options.id - The ID of the user.
 * @returns {Object} The user object.
 */
export async function getUser({ id }) {
  const user = await getCustomer({ id: id })
  return {
    ...user,
    customerGroup: user?.customerGroup.obj,
    role: getRole({ customerGroup: user?.customerGroup.obj }),
    employeeNumber: user?.custom?.fields?.employeeNumber,
    customAdmFields: {
      admNumbers: user?.custom?.fields?.admNumbers,
      regionalLeadNumbers: user?.custom?.fields?.regionalLeadNumbers,
      allowReservationMutation: user?.custom?.fields?.allowReservationMutation,
      allowAllCustomerSearch: user?.custom?.fields?.allowAllCustomerSearch,
      allowDocumentMutation: user?.custom?.fields?.allowDocumentMutation,
      enableAssistant: user?.custom?.fields?.enableAssistant,
      altBranchCustomerSearch: user?.custom?.fields?.altBranchCustomerSearch,
    },
  }
}

/**
 * Get the assigned customer groups for a user.
 *
 * @param {Object} options
 * @param {string} options.employeeNumber - The employee number of the user.
 * @param {number} options.limit - The maximum number of results to return.
 * @param {number} options.offset - The number of results to skip.
 * @param {Object} options.sort - The sorting options.
 * @param {string} options.sort.orderBy - The field to sort by.
 * @param {string} options.sort.direction - The sort direction.
 * @returns {Object}
 */
export const getAssignedCustomerGroups = async ({ employeeNumber, limit, offset, sort }) => {
  const queryArgs = {
    limit,
    offset,
    sort: sort ? `${sort.orderBy} ${sort.direction}` : null,
    where: `custom(fields(regionalLeadNumbers contains any ("${employeeNumber}")))`,
  }

  const response = (await commerceApi.customers().get({ queryArgs }).execute())?.body

  if (!response) {
    return { customerGroupData: [], total: 0 }
  }

  const adm = await getAdm({ admNumber: employeeNumber })
  const excludedCustomerGroups = adm?.custom?.fields?.regionalLeadExludedCustomerGroups

  const customerGroupData = await getGroupData(response?.results, excludedCustomerGroups)

  return { customerGroupData, total: response?.total }
}

/**
 * Get the assigned ADM numbers for a user.
 *
 * @param {Object} options
 * @param {string} options.employeeNumber - The employee number of the user.
 * @returns {Object}
 */
export const getAssignedAdmNumbers = async ({ employeeNumber }) => {
  const queryArgs = {
    limit: 500,
    where: `custom(fields(regionalLeadNumbers contains any ("${employeeNumber}")))`,
  }

  const results = (await commerceApi.customers().get({ queryArgs }).execute())?.body?.results

  if (!results || results?.length < 1) {
    return { assignedAdmNumbers: [] }
  }

  // Get all ADM numbers without duplicates and empty values
  let assignedAdms = [...new Set(results.flatMap((adm) => adm?.custom?.fields?.admNumbers).filter(Boolean))]

  // Filter out excluded customer groups for regional leads - EINZA-1647
  const adm = await getAdm({ admNumber: employeeNumber })
  const excludedCustomerGroups = adm?.custom?.fields?.regionalLeadExludedCustomerGroups
  assignedAdms = assignedAdms?.filter((adm) => !excludedCustomerGroups?.includes(adm))

  return { assignedAdmNumbers: assignedAdms || [] }
}

/**
 * Get all ADMs assigned to an admin.
 *
 * @returns {Object}
 */
export const getAllAdmNumbers = async () => {
  const [admCustomerGroup, regionalLeadCustomerGroup, admGvlGroup] = await Promise.all([
    getCustomerGroup({ key: "adm_user" }),
    getCustomerGroup({ key: "adm_regionallead" }),
    getCustomerGroup({ key: "adm_gvl" }),
  ])

  const queryArgs = {
    limit: 500,
    where: `customerGroup(id="${admCustomerGroup?.id}" or id="${regionalLeadCustomerGroup?.id}" or id="${admGvlGroup?.id}")`,
  }

  const results = (await commerceApi.customers().get({ queryArgs }).execute())?.body?.results

  if (!results || results?.length < 1) {
    return { assignedAdmNumbers: [] }
  }

  // Get all ADM numbers without duplicates and empty values
  const assignedAdmNumbers = [...new Set(results.flatMap((adm) => adm?.custom?.fields?.admNumbers).filter(Boolean))]

  return { assignedAdmNumbers }
}

/**
 * Get all customer groups assigned to an admin.
 *
 * @param {Object} options
 * @param {number} options.limit - The maximum number of results to return.
 * @param {number} options.offset - The number of results to skip.
 * @param {Object} options.sort - The sorting options.
 * @returns {Object}
 */
export const getAllCustomerGroups = async ({ limit, offset, sort } = {}) => {
  const [admCustomerGroup, regionalLeadCustomerGroup, admGvlGroup] = await Promise.all([
    getCustomerGroup({ key: "adm_user" }),
    getCustomerGroup({ key: "adm_regionallead" }),
    getCustomerGroup({ key: "adm_gvl" }),
  ])

  const queryArgs = {
    limit,
    offset,
    sort: sort ? `${sort.orderBy} ${sort.direction}` : null,
    where: `customerGroup(id="${admCustomerGroup?.id}" or id="${regionalLeadCustomerGroup?.id}" or id="${admGvlGroup?.id}")`,
  }

  const response = (await commerceApi.customers().get({ queryArgs }).execute())?.body

  if (!response) {
    return { customerGroupData: [], total: 0 }
  }

  const customerGroupData = await getGroupData(response?.results)

  return { customerGroupData, total: response?.total }
}

/**
 * Get the group data for a list of ADMs.
 *
 * @param {Object[]} results - The results from the query.
 * @param {string[]} excludedCustomerGroups - The excluded customer groups.
 * @returns {Object[]} The customer group data.
 */
const getGroupData = async (results, excludedCustomerGroups) => {
  let assignedAdms =
    results?.flatMap((adm) =>
      adm?.custom?.fields?.admNumbers?.map((admNumber) => ({
        admNumber,
        name: `${adm?.firstName} ${adm?.lastName}`,
      })),
    ) || []

  if (assignedAdms?.length < 1) {
    return []
  }

  // Filter out excluded customer groups for regional leads - EINZA-1647
  if (excludedCustomerGroups && excludedCustomerGroups.length > 0) {
    assignedAdms = assignedAdms?.filter((adm) => !excludedCustomerGroups.includes(adm.admNumber))
  }

  return await Promise.all(
    assignedAdms?.map(async ({ admNumber, name }) => ({
      ...(await getAdmSummary({ admNumber })),
      name,
    })),
  )
}

/**
 * Get a customer group by its key.
 * @param {Object} options - The options for getting the customer group.
 * @param {string} options.key - The key of the customer group.
 * @returns {Object} The customer group object.
 */
export const getCustomerGroup = async ({ key }) => {
  const customerGroup = await commerceApi.customerGroups().withKey({ key: key }).get().execute()

  return customerGroup.body
}

/**
 * Get an ADM by its ADM number.
 * @param {Object} options - The options for getting the ADM.
 * @param {string} options.admNumber - The ADM number.
 * @returns {Object} The response body.
 */
export const getAdm = async ({ admNumber }) => {
  const response = await commerceApi
    .customers()
    .get({
      queryArgs: {
        where: `custom(fields(admNumbers contains any ("${admNumber}")))`,
      },
    })
    .execute()

  return response?.body?.results[0]
}

/**
 * Get the summary of an ADM.
 * @param {Object} options - The options for getting the ADM summary.
 * @param {string} options.admNumber - The ADM number.
 * @returns {Object} The ADM summary.
 * @throws {Error} If the user does not have an ADM number.
 */
export async function getAdmSummary({ admNumber }) {
  if (!admNumber) {
    throw new Error("User does not have an adm number")
  }

  let lastYear,
    admSummary,
    customerWithSalesGrowth = null

  const admCustomers = await getAdmAssignedCustomers({ admNumber })

  const year = new Date().getFullYear()

  const graphiQLQueryObject = [
    {
      method: "admSummary",
      variables: { admNumber, year },
      fields: ["revenueTarget", "countCustomersWithSales"],
    },
    {
      method: "lastYear: admSummary",
      variables: { admNumber, year: year - 1 },
      fields: ["countCustomersWithSales"],
    },
  ]

  try {
    const query = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const queryResponse = (await EinzaGraphiQLService.getData(query))?.data

    lastYear = queryResponse?.lastYear
    admSummary = queryResponse?.admSummary

    customerWithSalesGrowth = Number(admSummary?.countCustomersWithSales) - Number(lastYear?.countCustomersWithSales)
  } catch (error) {
    console.error("getAdmSummary()", { error, admNumber })
  }

  return {
    admNumber,
    amountOfCustomers: admCustomers?.total ?? 0,
    amountOfCustomersWithSales: admSummary?.countCustomersWithSales ?? 0,
    revenueTarget: admSummary?.revenueTarget ?? null,
    customerWithSalesGrowth,
  }
}

/**
 * Retrieves branch id by user number
 *
 * @param {{ userNumber: string, client: SanityClient }}
 *
 * @returns {Promise<string>}
 */
export const getUserBranchId = async ({ userNumber, client }) => {
  const draftsFilter = `&& !(_id in path("drafts.**"))`

  const employee = await client.fetch(
    groq`*[_type=="employee" && key==$key ${draftsFilter}][0] {
          "office": branchOffice->{id}
        }`,
    { key: userNumber },
  )

  return employee?.office?.id
}
